import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { Helmet } from "react-helmet-async";
import PropTypes from "prop-types";
import {
  Box,
  Stack,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Skeleton,
  InputAdornment,
  Popover,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  MenuItem,
  Alert,
  CircularProgress,
  Modal,
  Slide} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Schedule as ScheduleIcon,
  Description as DescriptionIcon,
} from "@mui/icons-material";
import { RangePicker } from "react-minimal-datetime-range";
import "../../../../vendor/react-minimal-datetime-range.css";
import { ReactComponent as FilterIcon } from "../../../../vendor/_new/icon/sites/filter.svg";
import { ReactComponent as UserIcon } from "../../../../vendor/_new/icon/navbar/account/profile.svg";
import { ReactComponent as ActionIcon } from "../../../../vendor/_new/icon/sites/edit.svg";
import { ReactComponent as ResourceIcon } from "../../../../vendor/_new/icon/sidebar/services/config.svg";
import { ReactComponent as StatusIcon } from "../../../../vendor/_new/icon/detections/response_status.svg";
import colors from "../../../../utils/colors";
import V2PageHeader from "../../../common/V2PageHeader";
import RefreshIconButton from "../../../common/RefreshIconButton";
import axios from "../../../../utils/axios/v1/userAxios";
import ReactPerfectScrollbar from "react-perfect-scrollbar";
import copy from "copy-to-clipboard";
import { formatDate } from "../../../../utils/format";
import PermissionGate from "../../../guards/PermissionGate";
import usePermissions from "../../../../hooks/usePermissions";
import { PERMISSIONS } from "../../../../utils/permissions";
// import { ReactComponent as CloseIcon } from "../../../../vendor/_new/icon/navbar/notification/close_big.svg";
import { ReactComponent as CopyIcon } from "../../../../vendor/_new/icon/sites/copy.svg";
import { SafeTextField } from "../../../common/Greens";

const brandBlue = colors.primary[300];

const timeRangeOptions = [
  { value: "30m", label: "Last 30 minutes" },
  { value: "1h", label: "Last hour" },
  { value: "6h", label: "Last 6 hours" },
  { value: "24h", label: "Last 24 hours" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "custom", label: "Custom range" },
];

const getTimeRangeLabel = (value, customRange) => {
  if (value === "custom" && customRange) {
    const formatDate = (dateStr) => {
      const d = new Date(dateStr);
      return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    };
    return `${formatDate(customRange[0])} - ${formatDate(customRange[1])}`;
  }
  const option = timeRangeOptions.find((o) => o.value === value);
  return option ? option.label : "Select time range";
};

const TimeRangeFilter = ({ value, customRange, onChange, onCustomRangeChange }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const open = Boolean(anchorEl);

  const handleSelect = (selectedValue) => {
    if (selectedValue === "custom") {
      setShowCustomPicker(true);
    } else {
      onChange(selectedValue);
      setAnchorEl(null);
    }
  };

  const handleCustomConfirm = (res) => {
    const fromDate = res[0].split(" ")[0] + "T" + res[0].split(" ")[1] + ":00";
    const toDate = res[1].split(" ")[0] + "T" + res[1].split(" ")[1] + ":00";
    onCustomRangeChange([fromDate, toDate]);
    onChange("custom");
    setShowCustomPicker(false);
    setAnchorEl(null);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(null);
  };

  const displayLabel = value ? getTimeRangeLabel(value, customRange) : "Time Range";

  return (
    <>
      <Box
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.5,
          py: 1,
          borderRadius: "6px",
          border: `1px solid ${value ? brandBlue : colors.neutral[200]}`,
          backgroundColor: value ? colors.primary[100] : "white",
          cursor: "pointer",
          minWidth: 140,
          "&:hover": {
            borderColor: brandBlue,
            backgroundColor: colors.primary[100],
            "& .filter-label": {
              color: brandBlue,
            },
          },
        }}
      >
        <ScheduleIcon sx={{ width: 16, height: 16, color: brandBlue }} />
        <Typography
          className="filter-label"
          sx={{
            fontSize: 14,
            lineHeight: "21px",
            color: value ? brandBlue : colors.neutral[800],
            flex: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {displayLabel}
        </Typography>
        {value ? (
          <CloseIcon
            onClick={handleClear}
            sx={{ fontSize: 14, color: brandBlue, "&:hover": { color: colors.red[100] } }}
          />
        ) : (
          <ArrowDownIcon sx={{ fontSize: 16, color: brandBlue }} />
        )}
      </Box>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
          setShowCustomPicker(false);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: "8px",
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.12)",
            border: `1px solid ${colors.neutral[200]}`,
            width: showCustomPicker ? 700 : 200,
            minWidth: showCustomPicker ? 700 : "auto",
            overflow: "visible",
            backgroundColor: "white",
            "& .react-minimal-datetime-range": {
              display: "flex !important",
              flexDirection: "row !important",
              flexWrap: "nowrap !important",
              gap: "16px",
              alignItems: "flex-start",
            },
            "& .react-minimal-datetime-range__calendar-wrapper": {
              display: "flex !important",
              flexDirection: "row !important",
              flexWrap: "nowrap !important",
              gap: "16px",
            },
            "& .react-minimal-datetime-range__calendar": {
              marginBottom: "0 !important",
              display: "inline-block !important",
              marginRight: "16px",
              verticalAlign: "top",
            },
            "& .react-minimal-datetime-range-calendar--range": {
              display: "inline-block !important",
              marginRight: "16px",
            },
          },
        }}
      >
        {showCustomPicker ? (
          <Box sx={{ p: 2 }}>
            <Typography sx={ { fontSize: 13, color: colors.neutral[800], mb: 1.5 }}>
              Select custom date range
            </Typography>
            <Box 
              className="custom-date-range-picker"
              sx={{ 
                display: "flex",
                flexDirection: "column",
                width: "100%",
                "& .react-minimal-datetime-range": {
                  display: "flex !important",
                  flexDirection: "row !important",
                  flexWrap: "nowrap !important",
                  gap: "16px !important",
                  alignItems: "flex-start !important",
                  width: "100% !important",
                  whiteSpace: "nowrap !important",
                },
                "& .react-minimal-datetime-range__calendar-wrapper": {
                  display: "flex !important",
                  flexDirection: "row !important",
                  flexWrap: "nowrap !important",
                  gap: "16px !important",
                  width: "auto !important",
                },
                "& .react-minimal-datetime-range__calendar": {
                  display: "inline-block !important",
                  marginBottom: "0 !important",
                  marginRight: "16px !important",
                  verticalAlign: "top !important",
                  float: "none !important",
                  flexShrink: "0 !important",
                },
                "& .react-minimal-datetime-range-calendar--range": {
                  display: "inline-block !important",
                  marginRight: "16px !important",
                  float: "none !important",
                  flexShrink: "0 !important",
                },
              }}
            >
              <Box
                sx={{
                  fontSize: 0,
                  "& .react-minimal-datetime-range": {
                    display: "flex !important",
                    flexDirection: "row !important",
                    fontSize: "13px",
                  },
                  "& .react-minimal-datetime-range__calendar": {
                    marginBottom: "0 !important",
                    fontSize: "13px",
                  },
                }}
              >
                <RangePicker
                locale="en-us"
                show={true}
                disabled={false}
                allowPageClickToClose={false}
                onConfirm={handleCustomConfirm}
                onClose={() => setShowCustomPicker(false)}
                placeholder={["Start Time", "End Time"]}
                showOnlyTime={false}
                defaultDates={customRange ? [customRange[0].split("T")[0], customRange[1].split("T")[0]] : undefined}
                defaultTimes={customRange ? [customRange[0].split("T")[1]?.substring(0, 5) || "00:00", customRange[1].split("T")[1]?.substring(0, 5) || "23:59"] : ["00:00", "23:59"]}
                />
              </Box>
            </Box>
          </Box>
        ) : (
          <List disablePadding dense>
            {timeRangeOptions.map((option) => (
              <MenuItem
                key={option.value}
                onClick={() => handleSelect(option.value)}
                selected={value === option.value}
                sx={{
                  py: 1,
                  px: 2,
                  fontSize: 13,
                  color: colors.neutral[800],
                  "&:hover": { backgroundColor: colors.primary[50], color: brandBlue },
                  "&.Mui-selected": { backgroundColor: colors.primary[100], color: brandBlue },
                }}
              >
                {option.label}
              </MenuItem>
            ))}
          </List>
        )}
      </Popover>
    </>
  );
};

TimeRangeFilter.propTypes = {
  value: PropTypes.string,
  customRange: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  onCustomRangeChange: PropTypes.func.isRequired,
};


const predefinedFilters = [
  { id: "user", label: "User", icon: UserIcon, placeholder: "Filter by user" },
  { id: "action", label: "Action", icon: ActionIcon, placeholder: "Filter by action", options: ["Login", "Create", "Update", "Delete", "Export", "Backup"] },
  { id: "resource", label: "Resource", icon: ResourceIcon, placeholder: "Filter by resource" },
  { id: "status", label: "Status", icon: StatusIcon, placeholder: "Filter by status", options: ["Success", "Failed"] },
];

const PredefinedFilterDropdown = ({ filter, logs, value, onChange, onClear }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const open = Boolean(anchorEl);
  const IconComponent = filter.icon;

  useEffect(() => {
    if (open) {
      setInputValue(value || "");
    }
  }, [open, value]);

  const uniqueValues = useMemo(() => {
    if (filter.options) return filter.options;
    if (!logs) return [];
    const values = new Set();
    logs.forEach((log) => {
      const val = log[filter.id];
      if (val !== undefined && val !== null && val !== "") {
        values.add(String(val));
      }
    });
    return Array.from(values).slice(0, 15);
  }, [logs, filter]);

  const filteredValues = useMemo(() => {
    if (!inputValue) return uniqueValues;
    return uniqueValues.filter((v) =>
      String(v).toLowerCase().includes(inputValue.toLowerCase())
    );
  }, [inputValue, uniqueValues]);

  const handleApply = (selectedValue) => {
    onChange(filter.id, selectedValue);
    setAnchorEl(null);
    setInputValue("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && inputValue.trim()) {
      handleApply(inputValue.trim());
    }
  };

  return (
    <>
      <Box
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.5,
          py: 1,
          borderRadius: "6px",
          border: `1px solid ${value ? brandBlue : colors.neutral[200]}`,
          backgroundColor: value ? colors.primary[100] : "white",
          cursor: "pointer",
          minWidth: 120,
          "&:hover": {
            borderColor: brandBlue,
            backgroundColor: colors.primary[100],
            "& .filter-label": {
              color: brandBlue,
            },
          },
        }}
      >
        <IconComponent style={{ width: 16, height: 16, color: brandBlue }} />
        <Typography
          className="filter-label"
          sx={{
            fontSize: 14,
            lineHeight: "21px",
            color: value ? brandBlue : colors.neutral[800],
            flex: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {value || filter.label}
        </Typography>
        {value ? (
          <CloseIcon
            onClick={(e) => {
              e.stopPropagation();
              onClear(filter.id);
            }}
            sx={{ fontSize: 14, color: brandBlue, "&:hover": { color: colors.red[100] } }}
          />
        ) : (
          <ArrowDownIcon sx={{ fontSize: 16, color: brandBlue }} />
        )}
      </Box>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
          setInputValue("");
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: "8px",
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.12)",
            border: `1px solid ${colors.neutral[200]}`,
            width: 240,
            maxHeight: 320,
            backgroundColor: "white",
          },
        }}
      >
        <Box sx={{ p: 1.5 }}>
          <SafeTextField
            fullWidth
            placeholder={filter.placeholder}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            size="small"
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: colors.neutral[400] }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                height: 36,
                borderRadius: "6px",
                fontSize: 14,
                lineHeight: "21px",
                "& fieldset": { borderColor: colors.neutral[200] },
                "&.Mui-focused fieldset": { borderColor: colors.primary[300] },
              },
              "& .MuiOutlinedInput-input": {
                fontSize: 14,
                lineHeight: "21px",
              },
              "& .MuiOutlinedInput-input::placeholder": {
                fontSize: 14,
                lineHeight: "21px",
              },
            }}
          />
        </Box>
        <Divider />
        <Box sx={{ maxHeight: 200, overflow: "auto" }}>
          <List disablePadding dense>
            {filteredValues.length > 0 ? (
              filteredValues.map((val, idx) => (
                <ListItem key={idx} disablePadding>
                  <ListItemButton
                    onClick={() => handleApply(val)}
                    sx={{
                      py: 0.75,
                      px: 1.5,
                      "&:hover": { backgroundColor: colors.primary[50] },
                    }}
                  >
                    <ListItemText
                      primary={val}
                      primaryTypographyProps={{
                        sx:  { fontSize: 14, lineHeight: "21px", color: colors.neutral[800] },
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))
            ) : (
              <Box sx={{ p: 2, textAlign: "center" }}>
                <Typography sx={ { fontSize: 12, color: colors.neutral[500] }}>
                  {inputValue ? "No matches found" : "No values available"}
                </Typography>
              </Box>
            )}
          </List>
        </Box>
        {inputValue && !filteredValues.includes(inputValue) && (
          <>
            <Divider />
            <Box sx={{ p: 1 }}>
              <ListItemButton
                onClick={() => handleApply(inputValue)}
                sx={{
                  py: 0.75,
                  px: 1.5,
                  borderRadius: "4px",
                  backgroundColor: colors.primary[50],
                  "&:hover": { backgroundColor: colors.primary[100] },
                }}
              >
                <ListItemIcon sx={{ minWidth: 28 }}>
                  <AddIcon sx={{ fontSize: 16, color: colors.primary[300] }} />
                </ListItemIcon>
                <ListItemText
                  primary={`Use "${inputValue}"`}
                  primaryTypographyProps={{
                    sx:  { fontSize: 14, lineHeight: "21px", color: colors.primary[300], fontWeight: 500 },
                  }}
                />
              </ListItemButton>
            </Box>
          </>
        )}
      </Popover>
    </>
  );
};

PredefinedFilterDropdown.propTypes = {
  filter: PropTypes.object.isRequired,
  logs: PropTypes.array,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
};

// Shown above the filter bar when the user landed via global-search deep-
// link (`?q=foo`). Doesn't filter on its own — the existing pill bar is
// authoritative — but tells the user where they came from so they can
// translate the term into a structured filter.
const DeepLinkSearchBanner = () => {
  const [q, setQ] = React.useState(() => {
    try { return new URLSearchParams(window.location.search).get("q") || ""; } catch (_) { return ""; }
  });
  if (!q) return null;
  return (
    <Box sx={{
      display: "flex", alignItems: "center", gap: 1.5,
      backgroundColor: "#EEF0FE", border: "1px solid #C8D0FA", borderRadius: "8px",
      px: 2, py: 1.25, mb: 2,
    }}>
      <SearchIcon style={{ width: 16, height: 16, color: colors.primary[300] }} />
      <Typography sx={ { fontSize: 13, color: colors.neutral[1000] }}>
        Searching for <Box component="span" sx={{ fontWeight: 600 }}>"{q}"</Box> from global search.
        Use the filters below to refine.
      </Typography>
      <Box sx={{ flex: 1 }} />
      <IconButton size="small" onClick={() => {
        const u = new URL(window.location.href); u.searchParams.delete("q");
        window.history.replaceState({}, "", u.toString());
        setQ("");
      }}>
        <CloseIcon style={{ width: 14, height: 14, color: colors.neutral[600] }} />
      </IconButton>
    </Box>
  );
};

const AuditFilterBar = ({ activeFilters, onFiltersChange, logs, onRefresh, loading, timeRange, customRange, onTimeRangeChange, onCustomRangeChange }) => {
  const [predefinedValues, setPredefinedValues] = useState({});

  useEffect(() => {
    const newPredefinedValues = {};
    predefinedFilters.forEach((pf) => {
      const activeFilter = activeFilters.find((af) => af.id === pf.id);
      if (activeFilter && activeFilter.value) {
        newPredefinedValues[pf.id] = activeFilter.value;
      }
    });
    setPredefinedValues(newPredefinedValues);
  }, [activeFilters]);

  const handlePredefinedFilterChange = (filterId, value) => {
    setPredefinedValues((prev) => ({ ...prev, [filterId]: value }));
    const existingIndex = activeFilters.findIndex((f) => f.id === filterId);
    const filterDef = predefinedFilters.find((f) => f.id === filterId);
    if (existingIndex >= 0) {
      const newFilters = activeFilters.map((f) =>
        f.id === filterId ? { ...f, value, label: filterDef?.label || f.label } : f
      );
      onFiltersChange(newFilters);
    } else {
      const newFilters = [...activeFilters, { id: filterId, label: filterDef?.label || filterId, value }];
      onFiltersChange(newFilters);
    }
  };

  const handlePredefinedFilterClear = (filterId) => {
    setPredefinedValues((prev) => {
      const newValues = { ...prev };
      delete newValues[filterId];
      return newValues;
    });
    const newFilters = activeFilters.filter((f) => f.id !== filterId);
    onFiltersChange(newFilters);
  };

  const handleClearAll = () => {
    setPredefinedValues({});
    onFiltersChange([]);
    onTimeRangeChange(null);
  };

  const hasActiveFilters = activeFilters.length > 0 || timeRange;

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        p: 2,
        backgroundColor: colors.neutral[50],
        borderRadius: "12px",
        border: `1px solid ${colors.neutral[200]}`,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexWrap: "wrap", gap: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.5,
            py: 1,
          }}
        >
          <FilterIcon style={{ width: 16, height: 16, color: brandBlue }} />
          <Typography sx={ { fontSize: 14, fontWeight: "medium", color: colors.neutral[800] }}>
            Filters
          </Typography>
        </Box>
        {predefinedFilters.map((filter) => (
          <PredefinedFilterDropdown
            key={filter.id}
            filter={filter}
            logs={logs}
            value={predefinedValues[filter.id] || ""}
            onChange={handlePredefinedFilterChange}
            onClear={handlePredefinedFilterClear}
          />
        ))}
        {hasActiveFilters && (
          <Chip
            label="Clear all"
            size="small"
            onClick={handleClearAll}
            sx={{
              fontSize: 12,
              backgroundColor: colors.neutral[200],
              "&:hover": { backgroundColor: colors.neutral[300] },
            }}
          />
        )}
      </Stack>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <TimeRangeFilter
          value={timeRange}
          customRange={customRange}
          onChange={onTimeRangeChange}
          onCustomRangeChange={onCustomRangeChange}
        />
        <RefreshIconButton onClick={onRefresh} disabled={loading} loading={loading} size="compact" />
      </Stack>
    </Stack>
  );
};

AuditFilterBar.propTypes = {
  activeFilters: PropTypes.array.isRequired,
  onFiltersChange: PropTypes.func.isRequired,
  logs: PropTypes.array,
  onRefresh: PropTypes.func,
  loading: PropTypes.bool,
  timeRange: PropTypes.string,
  customRange: PropTypes.array,
  onTimeRangeChange: PropTypes.func.isRequired,
  onCustomRangeChange: PropTypes.func.isRequired,
};

const getActionColor = (action) => {
  switch (action) {
    case "Create":
      return colors.green[300];
    case "Update":
      return colors.primary[300];
    case "Delete":
      return colors.red[100];
    case "Login":
      return colors.yellow[100];
    case "Export":
      return colors.neutral[600];
    case "Backup":
      return colors.neutral[600];
    default:
      return colors.neutral[600];
  }
};

function SkeletonContent({ rowsPerPage }) {
  const rowArray = [];
  for (let i = 0; i < rowsPerPage; i++) {
    rowArray.push(i);
  }

  const cellArray = [0, 1, 2, 3, 4, 5, 6];

  return (
    <TableBody>
      {rowArray.map((r) => (
        <TableRow key={"s_" + r}>
          {cellArray.map((c) => (
            <TableCell
              sx={{ padding: "16px 8px" }}
              key={"s_" + r + "_" + c}
            >
              <Skeleton
                height="22px"
                width="100%"
                py="5px"
                variant="rectangular"
                sx={{ borderRadius: "11px" }}
              />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </TableBody>
  );
}

SkeletonContent.propTypes = {
  rowsPerPage: PropTypes.number.isRequired,
};

const tableHeadSx =  { fontSize: 14, lineHeight: "21px", fontWeight: "medium", color: colors.neutral[1000] };
const tableBodySx =  { fontSize: 14, lineHeight: "21px", color: colors.neutral[800] };

// Detail Row Component
const DetailRow = ({ label, value, monospace = false, copyable = false, onCopy }) => {
  const handleCopy = () => {
    if (value && copyable) {
      copy(value);
      if (onCopy) onCopy(`${label} copied to clipboard`);
    }
  };

  return (
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ py: 1.5 }}>
      <Typography sx={ { fontSize: 14, color: colors.neutral[600], minWidth: 140 }}>
        {label}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, justifyContent: "flex-end" }}>
        <Typography
          sx={{
            ...(monospace ? { fontFamily: "monospace" } : {}),
            fontSize: 14,
            color: colors.neutral[1000],
            wordBreak: "break-word",
            textAlign: "right",
          }}
        >
          {value || "—"}
        </Typography>
        {copyable && value && (
          <IconButton size="small" onClick={handleCopy} sx={{ ml: 1 }}>
            <CopyIcon style={{ width: 16, height: 16, color: colors.neutral[600] }} />
          </IconButton>
        )}
      </Stack>
    </Stack>
  );
};

DetailRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  monospace: PropTypes.bool,
  copyable: PropTypes.bool,
  onCopy: PropTypes.func,
};

// Section Card Component
const SectionCard = ({ icon: Icon, title, children }) => (
  <Box
    sx={{
      backgroundColor: colors.neutral[50],
      borderRadius: "8px",
      border: `1px solid ${colors.neutral[200]}`,
      overflow: "hidden",
    }}
  >
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ p: 2, borderBottom: `1px solid ${colors.neutral[200]}` }}>
      {Icon && <Icon sx={{ width: 20, height: 20, color: colors.primary[300] }} />}
      <Typography sx={ { fontSize: 16, fontWeight: "medium", color: colors.neutral[1000] }}>
        {title}
      </Typography>
    </Stack>
    <Box sx={{ p: 3 }}>{children}</Box>
  </Box>
);

SectionCard.propTypes = {
  icon: PropTypes.elementType,
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

// Audit Log Detail Drawer Component
const AuditLogDetailDrawer = ({ open, onClose, log, loading }) => {
  const handleCopy = () => {
    // Copy functionality handled by copy-to-clipboard
  };

  if (!log && !loading) return null;

  return (
    <Modal open={open} onClose={onClose}>
      <Slide direction="left" in={open} mountOnEnter unmountOnExit>
        <Box
          sx={{
            position: "fixed",
            top: 0,
            right: 0,
            width: "33%",
            minWidth: 520,
            height: "100vh",
            backgroundColor: "#fff",
            boxShadow: "-4px 0 20px rgba(0,0,0,0.1)",
            zIndex: 1300,
            borderTopLeftRadius: "8px",
            outline: "none",
            border: "none",
          }}
        >
          <ReactPerfectScrollbar>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ p: 4, borderBottom: `1px solid ${colors.neutral[200]}` }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "8px",
                    backgroundColor: "#EEF0FE",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <DescriptionIcon sx={{ width: 28, height: 28, color: "#6371F7" }} />
                </Box>
                <Box>
                  <Typography sx={ { fontSize: 20, fontWeight: "medium", color: colors.neutral[1000] }}>
                    Audit Log
                  </Typography>
                  {log?.id && (
                    <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: colors.neutral[600] }}>
                      ID: {log.id.substring(0, 8)}...
                    </Typography>
                  )}
                </Box>
              </Stack>
              <IconButton onClick={onClose}>
                <CloseIcon style={{ width: 24, height: 24, color: colors.neutral[600] }} />
              </IconButton>
            </Stack>

            <Box sx={{ p: 4 }}>
              {loading ? (
                <Stack spacing={2}>
                  <Skeleton variant="rectangular" height={200} sx={{ borderRadius: "8px" }} />
                  <Skeleton variant="rectangular" height={200} sx={{ borderRadius: "8px" }} />
                </Stack>
              ) : log ? (
                <Stack spacing={3}>
                  <SectionCard icon={DescriptionIcon} title="Audit Log Details">
                    <Stack spacing={2}>
                      <DetailRow label="Time" value={formatDate(log.date || log.timestamp)} />
                      <DetailRow label="Client IP Address" value={log.ip_addr || log.ipAddress} monospace copyable onCopy={handleCopy} />
                      <DetailRow label="Action" value={log.action} />
                      {log.url && <DetailRow label="URL" value={log.url} monospace copyable onCopy={handleCopy} />}
                      {log.site_id && <DetailRow label="Site ID" value={log.site_id} monospace copyable onCopy={handleCopy} />}
                    </Stack>
                  </SectionCard>

                  {log.user && (
                    <SectionCard title="User Information">
                      <Stack spacing={2}>
                        {log.user.email && <DetailRow label="Email" value={log.user.email} copyable onCopy={handleCopy} />}
                        {log.user.username && <DetailRow label="Username" value={log.user.username} />}
                        {log.user.role !== undefined && (
                          <DetailRow label="Role" value={typeof log.user.role === "number" ? `Role ${log.user.role}` : log.user.role} />
                        )}
                      </Stack>
                    </SectionCard>
                  )}

                  {log.organisation && (
                    <SectionCard title="Organisation">
                      <Stack spacing={2}>
                        {log.organisation.title && <DetailRow label="Title" value={log.organisation.title} />}
                        {log.organisation.id && <DetailRow label="ID" value={log.organisation.id} monospace copyable onCopy={handleCopy} />}
                      </Stack>
                    </SectionCard>
                  )}

                  {log.params && Object.keys(log.params).length > 0 && (
                    <SectionCard title="Parameters">
                      <Box
                        sx={{
                          backgroundColor: "#FEF3C7",
                          borderRadius: "4px",
                          p: 2,
                          border: `1px solid #FCD34D`,
                        }}
                      >
                        <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: "#92400E", whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                          {JSON.stringify(log.params, null, 2)}
                        </Typography>
                      </Box>
                    </SectionCard>
                  )}

                  {log.administrator && Object.keys(log.administrator).length > 0 && (
                    <SectionCard title="Administrator">
                      <Box
                        sx={{
                          backgroundColor: colors.neutral[50],
                          borderRadius: "4px",
                          p: 2,
                          border: `1px solid ${colors.neutral[200]}`,
                        }}
                      >
                        <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: colors.neutral[800], whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                          {JSON.stringify(log.administrator, null, 2)}
                        </Typography>
                      </Box>
                    </SectionCard>
                  )}
                </Stack>
              ) : null}
            </Box>
          </ReactPerfectScrollbar>
        </Box>
      </Slide>
    </Modal>
  );
};

AuditLogDetailDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  log: PropTypes.object,
  loading: PropTypes.bool,
};

const AuditLogsPage = () => {
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState(null);
  const [allLogs, setAllLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [activeFilters, setActiveFilters] = useState([]);
  const [timeRange, setTimeRange] = useState(null);
  const [customRange, setCustomRange] = useState(null);
  const [error, setError] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [pageSize] = useState(50);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [logDetailLoading, setLogDetailLoading] = useState(false);
  
  const tableContainerRef = useRef(null);
  const sentinelRef = useRef(null);
  const isInitialLoadRef = useRef(true);
  const fetchAuditLogsRef = useRef(null);

  useEffect(() => {
    const now = new Date();
    const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    const formatDate = (d) => {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      const hours = String(d.getHours()).padStart(2, "0");
      const mins = String(d.getMinutes()).padStart(2, "0");
      return `${year}-${month}-${day}T${hours}:${mins}:00`;
    };
    setCustomRange([formatDate(oneDayAgo), formatDate(now)]);
    setTimeRange("7d");
  }, []);

  const buildConditions = useCallback(() => {
    const conditions = [];
    // Must match backend getAuditLogsSchema / auditlog service (LE-382).
    const CONTAINS = "cont";

    activeFilters.forEach((filter) => {
      if (!filter.value) return;
      let key = "";
      switch (filter.id) {
        case "user":
          key = "username";
          break;
        case "action":
          key = "action";
          break;
        case "resource":
          key = "resource";
          break;
        case "status":
          // AuditLog has no status column; mapLogs defaults all rows to Success.
          // Do not send to API (would fail Joi and surface as "Size is required").
          return;
        default:
          return;
      }
      conditions.push({ key, conditions: CONTAINS, value: String(filter.value) });
    });

    return conditions;
  }, [activeFilters]);

  const mapLogs = useCallback((data) => {
    return (data || []).map((log) => ({
      id: log.id || log._id || `${log.date}_${log.username}_${log.action}`,
      timestamp: log.date || log.timestamp,
      user: log.username || log.user || "Unknown",
      action: log.action || "Unknown",
      resource: log.resource || log.site_id || "System",
      resourceId: log.resource_id || log.site_id || "—",
      status: log.status || "Success",
      ipAddress: log.ip_addr || log.ipAddress || "N/A",
      userAgent: log.user_agent || log.userAgent || "",
      details: log.details || log.message || log.action || "No details available",
    }));
  }, []);

  const fetchAuditLogs = useCallback(async (from = 0, append = false) => {
    if (!append) {
      setLoading(true);
    } else {
      setIsLoadingMore(true);
    }
    setError(null);
    try {
      const conditions = buildConditions();
      const response = await axios.post("/log/audit", {
        site_id: "all",
        from,
        size: pageSize,
        conditions: conditions.length > 0 ? conditions : undefined,
      });

      let mappedLogs = mapLogs(response.data.data || []);
      const statusFilter = activeFilters.find((f) => f.id === "status" && f.value);
      if (statusFilter) {
        mappedLogs = mappedLogs.filter((log) => log.status === statusFilter.value);
      }
      const totalCount = statusFilter ? mappedLogs.length : response.data.total || 0;

      if (append) {
        setAllLogs((prev) => {
          const newLogs = [...prev, ...mappedLogs];
          setHasMore(!statusFilter && newLogs.length < (response.data.total || 0));
          return newLogs;
        });
      } else {
        setLogs(mappedLogs);
        setAllLogs(mappedLogs);
        setHasMore(
          !statusFilter &&
            mappedLogs.length < (response.data.total || 0) &&
            mappedLogs.length === pageSize
        );
      }
      setTotal(totalCount);
    } catch (err) {
      setError(err.message || "Failed to fetch audit logs");
      if (!append) {
        setLogs([]);
        setAllLogs([]);
      }
      setTotal(0);
      setHasMore(false);
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
    }
  }, [buildConditions, pageSize, mapLogs, activeFilters]);

  useEffect(() => {
    fetchAuditLogsRef.current = fetchAuditLogs;
  }, [fetchAuditLogs]);

  const filtersKey = useMemo(() => {
    return JSON.stringify(activeFilters.map(f => ({ id: f.id, value: f.value })).sort((a, b) => a.id.localeCompare(b.id)));
  }, [activeFilters]);

  useEffect(() => {
    setAllLogs([]);
    setHasMore(true);
    isInitialLoadRef.current = true;
    fetchAuditLogs(0, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey, timeRange, customRange]);

  useEffect(() => {
    if (logs !== null) {
      if (logs.length === 0 && allLogs.length > 0 && isInitialLoadRef.current) {
        setAllLogs([]);
        setHasMore(true);
        isInitialLoadRef.current = true;
      } else if (logs.length > 0) {
        if (isInitialLoadRef.current) {
          const initialLogs = logs.slice(0, pageSize);
          setAllLogs(initialLogs);
          setHasMore(initialLogs.length === pageSize && logs.length >= pageSize && initialLogs.length < total);
          isInitialLoadRef.current = false;
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logs, total, pageSize]);

  const handleFiltersChange = (newFilters) => {
    setActiveFilters(newFilters);
    setAllLogs([]);
    setHasMore(true);
    isInitialLoadRef.current = true;
  };

  const handleTimeRangeChange = (newTimeRange) => {
    setTimeRange(newTimeRange);
    setAllLogs([]);
    setHasMore(true);
    isInitialLoadRef.current = true;
  };

  const handleCustomRangeChange = (newCustomRange) => {
    setCustomRange(newCustomRange);
    setAllLogs([]);
    setHasMore(true);
    isInitialLoadRef.current = true;
  };

  const handleRefresh = () => {
    setAllLogs([]);
    setHasMore(true);
    isInitialLoadRef.current = true;
    fetchAuditLogs(0, false);
  };

  const handleLogClick = async (log) => {
    if (!log.id) return;
    
    setDrawerOpen(true);
    setLogDetailLoading(true);
    setSelectedLog(null);
    
    try {
      const response = await axios.get(`/log/audit/${log.id}`);
      setSelectedLog(response.data);
    } catch (err) {
      setError(err.message || "Failed to fetch audit log details");
      setSelectedLog(null);
    } finally {
      setLogDetailLoading(false);
    }
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setSelectedLog(null);
    setLogDetailLoading(false);
  };

  useEffect(() => {
    if (!hasMore || isLoadingMore || allLogs.length < pageSize) return;

    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const perfectScrollbar = document.querySelector(".ps");
    const root = perfectScrollbar || null;
    const rootMargin = "200px";

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !isLoadingMore && hasMore) {
          if (allLogs.length >= pageSize && allLogs.length < total) {
            setIsLoadingMore(true);
            const nextFrom = allLogs.length;
            if (fetchAuditLogsRef.current) {
              fetchAuditLogsRef.current(nextFrom, true);
            }
          }
        }
      },
      {
        root: root,
        rootMargin: rootMargin,
        threshold: 0.1,
      }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [hasMore, isLoadingMore, allLogs.length, total, pageSize]);

  return (
    <PermissionGate permission={PERMISSIONS.LOGS_READ} resourceLabel="audit logs">
    <>
      <Helmet title="Audit Logs" />
      <Box sx={{ p: 6 }}>
        <Stack spacing={4}>
          <Stack spacing={2}>
            <V2PageHeader
              title="Audit Logs"
              description="Track and monitor all system activities, user actions, and configuration changes."
              sx={{ pb: 0 }}
            />
          </Stack>

          {error && (
            <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <DeepLinkSearchBanner />

          <AuditFilterBar
            activeFilters={activeFilters}
            onFiltersChange={handleFiltersChange}
            logs={logs}
            onRefresh={handleRefresh}
            loading={loading}
            timeRange={timeRange}
            customRange={customRange}
            onTimeRangeChange={handleTimeRangeChange}
            onCustomRangeChange={handleCustomRangeChange}
          />

          <Box
            ref={tableContainerRef}
            sx={{
              backgroundColor: "#fff",
              borderRadius: "12px",
              border: `1px solid ${colors.neutral[200]}`,
              overflow: "hidden",
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: colors.neutral[50] }}>
                  <TableCell sx={{ ...tableHeadSx, pl: 3 }}>Timestamp</TableCell>
                  <TableCell sx={tableHeadSx}>User</TableCell>
                  <TableCell sx={tableHeadSx}>Action</TableCell>
                  <TableCell sx={tableHeadSx}>Resource</TableCell>
                  <TableCell sx={tableHeadSx}>Status</TableCell>
                  <TableCell sx={tableHeadSx}>IP Address</TableCell>
                  <TableCell sx={{ ...tableHeadSx, pr: 3 }}>Details</TableCell>
                </TableRow>
              </TableHead>
              {loading && logs === null ? (
                <SkeletonContent rowsPerPage={5} />
              ) : (
                <TableBody>
                  {allLogs.length > 0 ? (
                    allLogs.map((log) => (
                      <TableRow
                        key={log.id}
                        onClick={() => handleLogClick(log)}
                        sx={{
                          cursor: "pointer",
                          "&:hover": { backgroundColor: colors.neutral[50] },
                          transition: "background-color 0.15s",
                        }}
                      >
                        <TableCell sx={{ ...tableBodySx, pl: 3, whiteSpace: "nowrap" }}>
                          {formatDate(log.timestamp) || "—"}
                        </TableCell>
                        <TableCell sx={{ ...tableBodySx, whiteSpace: "nowrap" }}>
                          <Typography
                            sx={{
                              fontFamily: "monospace",
                              fontSize: 14,
                              color: colors.neutral[800],
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {log.user}
                          </Typography>
                        </TableCell>
                        <TableCell sx={tableBodySx}>
                          <Chip
                            label={log.action}
                            size="small"
                            sx={{
                              height: 24,
                              backgroundColor: `${getActionColor(log.action)}20`,
                              color: getActionColor(log.action),
                              fontSize: 14,
                              fontWeight: "medium",
                            }}
                          />
                        </TableCell>
                        <TableCell sx={tableBodySx}>
                          <Typography sx={ { fontSize: 14, color: colors.neutral[800] }}>
                            {log.resource}
                          </Typography>
                        </TableCell>
                        <TableCell sx={tableBodySx}>
                          <Chip
                            label={log.status}
                            size="small"
                            sx={{
                              height: 24,
                              backgroundColor: log.status === "Success" ? "#D1FAE5" : "#FEE2E2",
                              color: log.status === "Success" ? "#065F46" : "#991B1B",
                              fontSize: 14,
                              fontWeight: "medium",
                            }}
                          />
                        </TableCell>
                        <TableCell sx={tableBodySx}>
                          <Typography sx={{ fontFamily: "monospace", fontSize: 14, color: colors.neutral[800] }}>
                            {log.ipAddress}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ ...tableBodySx, pr: 3, maxWidth: 250 }}>
                          <Typography
                            sx={{
                              fontSize: 14,
                              color: colors.neutral[800],
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {log.details}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ textAlign: "center", py: 6 }}>
                        <Typography sx={ { fontSize: 14, color: colors.neutral[500] }}>
                          No audit logs match the selected filters
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              )}
            </Table>
            {hasMore && allLogs.length >= pageSize && <Box ref={sentinelRef} sx={{ height: 1, width: "100%" }} />}
            {isLoadingMore && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            )}
            {!hasMore && allLogs.length > 0 && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <Typography sx={ { fontSize: 13, color: colors.neutral[500] }}>
                  No more logs to load
                </Typography>
              </Box>
            )}
          </Box>

          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography sx={ { fontSize: 13, color: colors.neutral[600] }}>
              Showing {allLogs.length} of {total} entries
            </Typography>
          </Stack>
        </Stack>
      </Box>
      <AuditLogDetailDrawer
        open={drawerOpen}
        onClose={handleDrawerClose}
        log={selectedLog}
        loading={logDetailLoading}
      />
    </>
    </PermissionGate>
  );
};

export default AuditLogsPage;
